import * as React from "react";
import { cn } from "../utils";

/**
 * The sliding active marker shared by the app's vertical navigation rails — the
 * left settings nav and the right "On this page" rail (and visually matching the
 * primary ModeRail) — so the active item is indicated the SAME way everywhere: a
 * foreground bar that animates its `top` to the row marked `data-active="true"`,
 * riding the items' left guide line.
 *
 * Usage: attach `ref` to a `relative` container whose item rows carry
 * `data-active="true"` on the active one, and render `<NavRailMarker top={top} />`
 * inside it. Pass the active key as the hook arg so the marker re-measures when
 * the active row changes.
 */
export function useActiveMarker(activeKey?: string | null): {
  ref: React.RefObject<HTMLDivElement | null>;
  top: number | null;
} {
  const ref = React.useRef<HTMLDivElement>(null);
  const [top, setTop] = React.useState<number | null>(null);

  React.useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const measure = () => {
      const el = root.querySelector<HTMLElement>('[data-active="true"]');
      if (!el) {
        setTop(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setTop(r.top - root.getBoundingClientRect().top + r.height / 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [activeKey]);

  return { ref, top };
}

/** The marker bar itself — absolutely positioned at the guide line (left-0). */
export function NavRailMarker({
  top,
  className,
}: {
  top: number | null;
  className?: string;
}) {
  if (top == null) return null;
  return (
    <span
      aria-hidden
      className={cn(
        "pointer-events-none absolute left-0 h-3.5 w-0.5 -translate-y-1/2 rounded-full bg-foreground transition-[top] duration-300 ease-out motion-reduce:transition-none",
        className,
      )}
      style={{ top }}
    />
  );
}
